import React, { useState, useEffect } from 'react'
import Drawer from 'components/Drawer'
import Button from 'components/Button'

const POCDetailsDrawer = ({
  open,
  onClose,
  initialPOC,
  onSave,
  pocList = [],
  editingRecord,
  handleUpdateInstituteDetails,
}) => {
  // State for form visibility and editing
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [pocEditingIndex, setPocEditingIndex] = useState(null)

  // Form state
  const [poc, setPoc] = useState({
    name: '',
    number: '',
    email: '',
    isPrimary: false,
  })

  // Email error state
  const [emailError, setEmailError] = useState(false)
  // Validation error state
  const [formError, setFormError] = useState({
    name: '',
    number: '',
    email: '',
  })

  // Open form for add
  const handleAddPOC = () => {
    setPocEditingIndex(null)
    setPoc({
      name: '',
      number: '',
      email: '',
      isPrimary: pocList.length === 0,
    })
    setFormError({ name: '', number: '', email: '' })
    setIsFormOpen(true)
  }

  // Open form for edit
  const handleEditClick = idx => {
    setPocEditingIndex(idx)
    setPoc({ ...pocList[idx] })
    setFormError({ name: '', number: '', email: '' })
    setIsFormOpen(true)
  }

  // Save handler
  const handleSave = () => {
    let hasError = false
    let errors = { name: '', number: '', email: '' }
    if (!poc?.name) {
      errors.name = 'Name is required'
      hasError = true
    }
    if (!poc?.number) {
      errors.number = 'Number is required'
      hasError = true
    } else if (!/^[0-9]+$/.test(poc?.number)) {
      errors.number = 'Number must be numeric'
      hasError = true
    } else if (poc?.number?.length !== 10) {
      errors.number = 'Number must be 10 digit'
      hasError = true
    }
    if (!poc?.email) {
      errors.email = 'Email is required'
      hasError = true
    } else if (!/^\S+@\S+\.\S+$/.test(poc?.email)) {
      errors.email = 'Invalid email format'
      hasError = true
    }
    setFormError(errors)
    if (hasError) return

    // Unique email check (case-insensitive, trimmed)
    const emailExists = pocList.some(
      (item, idx) =>
        item.email.trim().toLowerCase() === poc.email.trim().toLowerCase() &&
        idx !== pocEditingIndex
    )
    if (emailExists) {
      setEmailError(true)
      return // Block save
    }
    setEmailError(false)

    if (pocEditingIndex !== null) {
      onSave({ poc, index: pocEditingIndex, action: 'update' })
    } else {
      onSave({ poc, action: 'add' })
    }
    setIsFormOpen(false)
    setPocEditingIndex(null)
    setPoc({ name: '', number: '', email: '', isPrimary: false })
    setFormError({ name: '', number: '', email: '' })
  }

  // On email input change, clear error if editing
  const handleEmailChange = e => {
    setPoc({ ...poc, email: e.target.value })
    if (emailError) setEmailError(false)
    if (formError.email) setFormError(prev => ({ ...prev, email: '' }))
  }

  // On number input change, only allow numbers
  const handleNumberChange = e => {
    const value = e.target.value.replace(/[^0-9]/g, '')
    setPoc({ ...poc, number: value })
    if (formError.number) setFormError(prev => ({ ...prev, number: '' }))
  }

  // Cancel handler
  const handleFormCancel = () => {
    if (pocEditingIndex !== null) {
      // Delete the card being edited: pass index and action
      onSave({ index: pocEditingIndex, action: 'delete' })
    }
    // If adding (pocEditingIndex === null), just close and clear form
    setIsFormOpen(false)
    setPocEditingIndex(null)
    setPoc({ name: '', number: '', email: '', isPrimary: false })
    setFormError({ name: '', number: '', email: '' })
  }

  // Card styles (move above usage)
  const cardStyle = {
    border: '1.5px solid #E3DEFF',
    borderRadius: 10,
    background: '#FBFAFF',
    padding: 24,
    marginBottom: 24,
    boxShadow: '0 2px 8px 0 rgba(50,73,215,0.04)',
  }
  const cardHeaderStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    marginBottom: 8,
  }
  const editButtonStyle = {
    padding: '0 16px',
    height: 32,
    fontWeight: 500,
    fontSize: 15,
    background: '#F6F3FF',
    color: '#7D64DA',
    border: 'none',
    borderRadius: 6,
  }
  const markedPrimaryButtonStyle = {
    padding: '0 16px',
    height: 32,
    fontWeight: 500,
    fontSize: 15,
    background: '#E3DEFF',
    color: '#B5A3F7',
    border: 'none',
    borderRadius: 6,
  }
  const contactTitleStyle = {
    fontWeight: 700,
    fontSize: 17,
    marginBottom: 8,
    color: '#181A1F',
  }
  const contactDetailStyle = {
    fontWeight: 500,
    fontSize: 15,
    marginBottom: 2,
    color: '#181A1F',
  }
  const emailStyle = {
    color: '#3249D7',
    textDecoration: 'underline',
    fontWeight: 500,
  }
  const dashedBoxStyle = {
    width: '95%',
    height: 55,
    border: '1px dashed #B5A3F7',
    borderRadius: 10,
    background: '#FBFAFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingTop: 10,
    paddingRight: 18,
    paddingBottom: 14,
    paddingLeft: 18,
    cursor: 'pointer',
    marginBottom: 24,
    marginTop: 0,
    boxSizing: 'border-box',
    borderStyle: 'dashed',
    borderWidth: 1,
  }
  const addPOCBtnTextStyle = {
    color: '#3249D7',
    fontWeight: 600,
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  }
  const inputStyle = {
    width: '100%',
    padding: '14px 18px',
    borderRadius: 10,
    border: '1px solid #E3DFFF',
    background: '#FAFAFB',
    fontSize: 16,
    marginBottom: 12,
    outline: 'none',
    fontWeight: 500,
  }
  const saveButtonStyle = {
    background: '#3249D7',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '10px 36px',
    fontWeight: 600,
    fontSize: 16,
    cursor: 'pointer',
    boxShadow: '0 2px 8px 0 rgba(50,73,215,0.08)',
  }
  const deleteButtonStyle = {
    background: '#fff',
    color: '#E35E50',
    border: '1px solid #E35E50',
    borderRadius: 8,
    padding: '10px 18px',
    fontWeight: 600,
    fontSize: 16,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px 0 rgba(227,94,80,0.08)',
  }
  const labelStyle = {
    fontWeight: 500,
    fontSize: 15,
    marginBottom: 8,
    color: '#323538',
  }
  const formFooterStyle = {
    display: 'flex',
    gap: 16,
    justifyContent: 'flex-end',
    marginTop: 8,
  }
  const drawerFooterStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 16,
    padding: '16px 0 0 0',
    background: 'transparent',
  }
  const cancelBtnStyle = {
    background: '#fff',
    color: '#3249D7',
    border: '1.5px solid #E3DEFF',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 16,
    padding: '10px 32px',
    cursor: 'pointer',
  }
  const updateBtnStyle = {
    background: '#3249D7',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 16,
    padding: '10px 32px',
  }

  // All POC Cards with inline form (hide card when editing)
  const allPOCCardsWithForm = pocList.map((item, idx) => (
    <React.Fragment key={idx}>
      {isFormOpen && pocEditingIndex === idx ? (
        <>
          <div
            style={{
              border: '1.5px dashed #B5A3F7',
              borderRadius: 14,
              background: '#FBFAFF',
              padding: 28,
              marginBottom: 24,
              marginTop: 0,
            }}
          >
            <div style={labelStyle}>POC Name / Designation</div>
            <input
              style={{
                ...inputStyle,
                border: formError.name
                  ? '1.5px solid #E35E50'
                  : inputStyle.border,
                background: formError.name ? '#FFF3F2' : inputStyle.background,
              }}
              placeholder="Enter POC name"
              value={poc.name}
              onChange={e => {
                setPoc({ ...poc, name: e.target.value })
                if (formError.name)
                  setFormError(prev => ({ ...prev, name: '' }))
              }}
            />
            {formError.name && (
              <div
                style={{
                  color: '#E35E50',
                  fontSize: 13,
                  marginTop: 4,
                  marginBottom: 8,
                  textAlign: 'left',
                }}
              >
                {formError.name}
              </div>
            )}
            <div style={labelStyle}>POC Number</div>
            <input
              style={{
                ...inputStyle,
                border: formError.number
                  ? '1.5px solid #E35E50'
                  : inputStyle.border,
                background: formError.number
                  ? '#FFF3F2'
                  : inputStyle.background,
              }}
              placeholder="Enter POC number"
              value={poc.number}
              onChange={handleNumberChange}
            />
            {formError.number && (
              <div
                style={{
                  color: '#E35E50',
                  fontSize: 13,
                  marginTop: 4,
                  marginBottom: 8,
                  textAlign: 'left',
                }}
              >
                {formError.number}
              </div>
            )}
            <div style={labelStyle}>POC Email</div>
            <input
              style={{
                ...inputStyle,
                border:
                  formError.email || emailError
                    ? '1.5px solid #E35E50'
                    : inputStyle.border,
                background:
                  formError.email || emailError
                    ? '#FFF3F2'
                    : inputStyle.background,
              }}
              placeholder="Enter POC email"
              value={poc.email}
              onChange={handleEmailChange}
            />
            {formError.email && (
              <div
                style={{
                  color: '#E35E50',
                  fontSize: 13,
                  marginTop: 4,
                  marginBottom: 8,
                  textAlign: 'left',
                }}
              >
                {formError.email}
              </div>
            )}
            <div style={{ marginBottom: 24, marginTop: 8 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 500,
                  fontSize: 15,
                }}
              >
                <input
                  type="checkbox"
                  checked={poc.isPrimary}
                  onChange={e =>
                    setPoc({ ...poc, isPrimary: e.target.checked })
                  }
                  disabled={pocList.length === 0}
                  style={{
                    width: 18,
                    height: 18,
                    accentColor: '#3249D7',
                    marginRight: 8,
                  }}
                />
                Mark as Primary
              </label>
            </div>
            <div style={formFooterStyle}>
              <button style={saveButtonStyle} onClick={handleSave}>
                Update
              </button>
              <button
                style={{
                  ...deleteButtonStyle,
                  background: '#FFE9E6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '10px 18px',
                  border: 'none',
                }}
                onClick={handleFormCancel}
              >
                {/* Use the original trash icon for all delete/clear actions */}
                <svg
                  width="18"
                  height="20"
                  viewBox="0 0 18 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 4V15.8C3 16.9201 3 17.4798 3.21799 17.9076C3.40973 18.2839 3.71547 18.5905 4.0918 18.7822C4.5192 19 5.07899 19 6.19691 19H11.8031C12.921 19 13.48 19 13.9074 18.7822C14.2837 18.5905 14.5905 18.2839 14.7822 17.9076C15 17.4802 15 16.921 15 15.8031V4M3 4H5M3 4H1M5 4H13M5 4C5 3.06812 5 2.60241 5.15224 2.23486C5.35523 1.74481 5.74432 1.35523 6.23438 1.15224C6.60192 1 7.06812 1 8 1H10C10.9319 1 11.3978 1 11.7654 1.15224C12.2554 1.35523 12.6447 1.74481 12.8477 2.23486C12.9999 2.6024 13 3.06812 13 4M13 4H15M15 4H17"
                    stroke="#E35E50"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
          {emailError && (
            <div
              style={{
                color: '#E35E50',
                fontSize: 13,
                marginTop: 8,
                marginBottom: 8,
                textAlign: 'left',
              }}
            >
              Email already exists
            </div>
          )}
        </>
      ) : (
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <button
              style={editButtonStyle}
              onClick={() => handleEditClick(idx)}
            >
              <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                <path
                  d="M2 11.5V14H4.5L11.8733 6.62667L9.37333 4.12667L2 11.5ZM13.8067 4.69333C14.0667 4.43333 14.0667 4.01333 13.8067 3.75333L12.2467 2.19333C11.9867 1.93333 11.5667 1.93333 11.3067 2.19333L10.0867 3.41333L12.5867 5.91333L13.8067 4.69333Z"
                  fill="#7D64DA"
                />
              </svg>{' '}
              Edit
            </button>
            {item.isPrimary && (
              <button style={markedPrimaryButtonStyle}>
                Marked as Primary
              </button>
            )}
          </div>
          <div style={contactTitleStyle}>Contact Details</div>
          <div style={contactDetailStyle}>
            POC Name / Designation : {item.name}
          </div>
          <div style={contactDetailStyle}>POC Number : {item.number}</div>
          <div style={contactDetailStyle}>
            POC Email :{' '}
            <a href={`mailto:${item.email}`} style={emailStyle}>
              {item.email}
            </a>
          </div>
        </div>
      )}
    </React.Fragment>
  ))

  // Add form at the end if adding
  const addForm = isFormOpen && pocEditingIndex === null && (
    <div
      style={{
        border: '1.5px dashed #B5A3F7',
        borderRadius: 14,
        background: '#FBFAFF',
        padding: 28,
        marginBottom: 24,
        marginTop: 0,
      }}
    >
      <div style={labelStyle}>POC Name / Designation</div>
      <input
        style={{
          ...inputStyle,
          border: formError.name ? '1.5px solid #E35E50' : inputStyle.border,
          background: formError.name ? '#FFF3F2' : inputStyle.background,
        }}
        placeholder="Enter POC name"
        value={poc.name}
        onChange={e => {
          setPoc({ ...poc, name: e.target.value })
          if (formError.name) setFormError(prev => ({ ...prev, name: '' }))
        }}
      />
      {formError.name && (
        <div
          style={{
            color: '#E35E50',
            fontSize: 13,
            marginTop: 4,
            marginBottom: 8,
            textAlign: 'left',
          }}
        >
          {formError.name}
        </div>
      )}
      <div style={labelStyle}>POC Number</div>
      <input
        style={{
          ...inputStyle,
          border: formError.number ? '1.5px solid #E35E50' : inputStyle.border,
          background: formError.number ? '#FFF3F2' : inputStyle.background,
        }}
        placeholder="Enter POC number"
        value={poc.number}
        onChange={handleNumberChange}
      />
      {formError.number && (
        <div
          style={{
            color: '#E35E50',
            fontSize: 13,
            marginTop: 4,
            marginBottom: 8,
            textAlign: 'left',
          }}
        >
          {formError.number}
        </div>
      )}
      <div style={labelStyle}>POC Email</div>
      <input
        style={{
          ...inputStyle,
          border:
            formError.email || emailError
              ? '1.5px solid #E35E50'
              : inputStyle.border,
          background:
            formError.email || emailError ? '#FFF3F2' : inputStyle.background,
        }}
        placeholder="Enter POC email"
        value={poc.email}
        onChange={handleEmailChange}
      />
      {formError.email && (
        <div
          style={{
            color: '#E35E50',
            fontSize: 13,
            marginTop: 4,
            marginBottom: 8,
            textAlign: 'left',
          }}
        >
          {formError.email}
        </div>
      )}
      <div style={{ marginBottom: 24, marginTop: 8 }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          <input
            type="checkbox"
            checked={poc.isPrimary}
            onChange={e => setPoc({ ...poc, isPrimary: e.target.checked })}
            disabled={pocList.length === 0}
            style={{
              width: 18,
              height: 18,
              accentColor: '#3249D7',
              marginRight: 8,
            }}
          />
          Mark as Primary
        </label>
      </div>
      <div style={formFooterStyle}>
        <button style={saveButtonStyle} onClick={handleSave}>
          {pocEditingIndex !== null ? 'Update' : 'Save'}
        </button>
        <button style={deleteButtonStyle} onClick={handleFormCancel}>
          {/* Use the original trash icon for all delete/clear actions */}
          <svg
            width="18"
            height="20"
            viewBox="0 0 18 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 4V15.8C3 16.9201 3 17.4798 3.21799 17.9076C3.40973 18.2839 3.71547 18.5905 4.0918 18.7822C4.5192 19 5.07899 19 6.19691 19H11.8031C12.921 19 13.48 19 13.9074 18.7822C14.2837 18.5905 14.5905 18.2839 14.7822 17.9076C15 17.4802 15 16.921 15 15.8031V4M3 4H5M3 4H1M5 4H13M5 4C5 3.06812 5 2.60241 5.15224 2.23486C5.35523 1.74481 5.74432 1.35523 6.23438 1.15224C6.60192 1 7.06812 1 8 1H10C10.9319 1 11.3978 1 11.7654 1.15224C12.2554 1.35523 12.6447 1.74481 12.8477 2.23486C12.9999 2.6024 13 3.06812 13 4M13 4H15M15 4H17"
              stroke="#E35E50"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  )

  const handleCancel = () => {
    onClose()
  }

  const content = () => (
    <div style={{ margin: '32px 0' }}>
      {/* Show all POC cards with inline form if editing */}
      {allPOCCardsWithForm}
      {/* Add button below cards */}
      {!isFormOpen && (
        <div style={dashedBoxStyle} onClick={handleAddPOC}>
          <span style={addPOCBtnTextStyle}>
            <svg width="24" height="24" fill="none" viewBox="0 0 32 32">
              <circle
                cx="16"
                cy="16"
                r="14"
                stroke="#3249D7"
                strokeWidth="3"
                fill="none"
              />
              <path
                d="M16 10V22"
                stroke="#3249D7"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <path
                d="M10 16H22"
                stroke="#3249D7"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
            Add POC Details
          </span>
        </div>
      )}
      {/* Show add form at the end if adding */}
      {addForm}
    </div>
  )

  const footer = () => (
    <div style={drawerFooterStyle}>
      <button style={cancelBtnStyle} onClick={handleCancel}>
        Cancel
      </button>
      <button
        style={{
          ...updateBtnStyle,
          cursor: !pocList || pocList.length === 0 ? 'pointer' : 'pointer',
        }}
        onClick={() => handleUpdateInstituteDetails(editingRecord, 0, pocList)}
        // disabled={!pocList || pocList.length === 0}
      >
        Update
      </button>
    </div>
  )

  return (
    <Drawer
      zIndex={7000}
      onClose={handleCancel}
      width="40%"
      padding="0px 30px"
      title={'Point of Contact (POC) Details'}
      placement="right"
      closable={true}
      content={content()}
      footer={footer()}
      visible={open}
    />
  )
}

export default POCDetailsDrawer
